import {Button, Card, DatePicker, Divider, Flex, Skeleton, Typography} from 'antd';
import {NoSearchResults, SnackBar, Wrapper} from 'Components';
import {useCSContext} from 'Context/ContentStackContext';
import dayjs from 'dayjs';
import {useFormik} from 'formik';
import {useSnackBar} from 'Hooks';
import React, {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {setTenantDetails} from 'Redux/Slices/login';
import iconSize from 'Theme/Icon';
import {
  agentRole,
  MEMBER_SEARCH_PAGE_SIZE,
  V2_ENABLED_CLIENTS,
} from 'Utils/constants/genericConstants';
import {getErrorMsg} from 'Utils/helpers/ErrorHandler';
import {trimFormValues} from 'Utils/helpers/SRDetailViewHelper';
import * as Yup from 'yup';
import {fetchMember} from '../../Redux/Slices/member';
import {getAllMetadata} from 'Redux/Slices/allMetadata';
import AntSearchField from './components/AntSearchField';
import MultipleResults from './components/MultipleResult';
import {MEMBER_SEARCH} from './searchConstants';
import styles from './styles';

const {Title, Text} = Typography;

const SearchMember = () => {
  const dispatch = useDispatch();
  const clientArray = useSelector((state: any) => state?.login?.loginInfo?.configs);
  const loginDetails = useSelector((state: any) => state?.login);
  const client = clientArray.map((item) => item['name']);
  const {artifacts} = useCSContext();
  const [loading, setLoading] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const [error, setError] = useState('');
  const [isSnackBarOpen, closeSnackBar] = useSnackBar(error);
  const [multipleResults, setMultipleResults] = useState<any>();
  const [pageNumber, setPageNumber] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const status = ['Active', 'Cancelled', 'Incomplete', 'Suspended', 'Unknown'];
  const role = useSelector((state: any) => state?.login?.loginInfo?.role);
  const searchResultRef = useRef<any>();

  const validationSchema = Yup.object({
    client: Yup.string().required(),
  });

  const formik: any = useFormik({
    validateOnChange: false,
    validateOnBlur: false,
    validationSchema,
    initialValues: {
      client: client?.[0],
    },
    onSubmit: async (values: any) => {
      setPageNumber(0);
      let formValues = trimFormValues(values);
      searchMember(formValues);
    },
  });
  const clearMessageOnClose = () => {
    setError('');
  };
  const _handleResponse = async (data, loading, errorObj, page) => {
    if (errorObj?.error) {
      setError(getErrorMsg(errorObj.error));
    } else {
      setMultipleResults((prevResults) => (page === 0 ? data : [...prevResults, ...data]));
      setHasMore(errorObj?.data?.searchMember?.paging?.isNextPagePresent);
    }
    setLoading(loading);
  };

  const searchMember = async (values, page = 0) => {
    if (loading) return; // Prevent increasing page count if loading
    setLoading(true);
    if (page === 0) {
      multipleResults && setMultipleResults(null);
      noResults && setNoResults(false);
    }
    const payload = getPayload(values, page);
    try {
      const response = await dispatch(fetchMember({payload})).unwrap();
      _handleResponse(response?.data?.searchMember?.memberDetails, loading, response, page);
    } catch (error) {
      console.warn('Error', error);
    }
  };

  const getPayload = (values, page) => {
    const selectedTenant = clientArray.filter((client) => client.name === values.client);

    const payload = {
      client: values.address,
      addressLine1: values.address || '',
      email: values.email || '',
      externalRef: values.externalId || '',
      postalCode: values.postCode || '',
      lastName: values.lastName || '',
      phone: values.phoneNumber || '',
      membershipNumber: values.membershipNumber || '',
      status: values.status || '',
      city: values.city || '',
      firstName: values.firstName || '',
      membershipEnabled:
        selectedTenant?.[0]?.tenantConfigs?.membershipEnabled === 'true' ? true : false,
      pageNumber: page.toString(),
      pageSize: MEMBER_SEARCH_PAGE_SIZE,
    };

    return payload;
  };

  const fetchMetaData = async () => {
    try {
      let response: any = await dispatch(getAllMetadata({})).unwrap();
      if (response?.error) {
        setError(getErrorMsg(response.error));
      }
    } catch (err) {
      console.log(err);
      setError(getErrorMsg(err));
      return err;
    }
  };

  useEffect(() => {
    dispatch(setTenantDetails(client[0]));
  }, []);

  useEffect(() => {
    if (pageNumber === 0 && multipleResults?.length > 0) {
      searchResultRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'});
    }
  }, [multipleResults]);

  useEffect(() => {
    (async () => {
      await fetchMetaData();
    })();
  }, [formik.values['client']]);

  const resetTenantDetails = async () => {
    await dispatch(setTenantDetails(client[0]));
  };

  const handleScroll = () => {
    if (
      (window.innerHeight + document.documentElement.scrollTop !==
        document.documentElement.offsetHeight &&
        window.innerHeight + document.documentElement.scrollTop + 1 <
          document.documentElement.offsetHeight) ||
      loading ||
      !hasMore
    )
      return;
    if (loading) return;
    const nextPage = pageNumber + 1;
    setPageNumber(nextPage);
    searchMember(formik.values, nextPage);
  };

  useEffect(() => {
    const onScroll = () => {
      if (!loading) {
        handleScroll();
      }
    };
    const onResize = () => {
      if (!loading) {
        handleScroll();
      }
    };
    window.addEventListener('scroll', onScroll);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, [loading, hasMore]);

  const isLevel1 = role === agentRole.level1;
  const isV2Disabled = V2_ENABLED_CLIENTS.includes(loginDetails?.tenantID);

  return (
    <Wrapper>
      <Card style={isLevel1 ? styles.card : styles.adminCard} styles={{body: {padding: 0}}}>
        {isLevel1 ? (
          <>
            <Flex align="center" gap={8} style={styles.topContainer}>
              <img src={artifacts.ast_icon} alt="ast icon" />
              <Title level={1} style={{margin: 0}}>
                {MEMBER_SEARCH.heading}
              </Title>
            </Flex>
            <Divider style={{margin: 0}} />
          </>
        ) : null}

        <Flex vertical gap={16} style={{padding: isLevel1 ? 48 : 32}}>
          <Title level={3} style={{margin: 0}}>
            {MEMBER_SEARCH.subtitle}
          </Title>
          {isLevel1 ? <Text>{MEMBER_SEARCH.member_search_text}</Text> : null}
          {!isLevel1 ? <Divider style={{margin: 0}} /> : null}

          <div style={styles.searchForm}>
            <AntSearchField
              label="Client"
              name="client"
              options={client}
              onChange={(e) => {
                dispatch(setTenantDetails(e.target.value));
                formik.handleChange(e);
              }}
              value={formik.values['client']}
            />
            <AntSearchField
              label={isLevel1 ? 'Member Identifier' : 'External Ref#'}
              name="externalId"
              onChange={formik.handleChange}
              value={formik.values['externalId']}
            />

            {!isLevel1 ? (
              <>
                <AntSearchField
                  label="Membership Number"
                  name="membershipNumber"
                  onChange={formik.handleChange}
                  value={formik.values['membershipNumber']}
                />
                <AntSearchField
                  label="Last Name"
                  name="lastName"
                  onChange={formik.handleChange}
                  value={formik.values['lastName']}
                />
                <AntSearchField
                  label="First Name"
                  name="firstName"
                  onChange={formik.handleChange}
                  value={formik.values['firstName']}
                />
                <AntSearchField
                  label="Email"
                  name="email"
                  onChange={formik.handleChange}
                  value={formik.values['email']}
                />
                <AntSearchField
                  label="Post Code"
                  name="postCode"
                  disabled={isV2Disabled}
                  onChange={formik.handleChange}
                  value={formik.values['postCode']}
                />
                <AntSearchField
                  label="City "
                  disabled={isV2Disabled}
                  name="city"
                  onChange={formik.handleChange}
                  value={formik.values['city']}
                />
                <AntSearchField
                  label="Address"
                  name="address"
                  disabled={isV2Disabled}
                  onChange={formik.handleChange}
                  value={formik.values['address']}
                />
                <Flex vertical gap={6} style={{flex: 1, minWidth: 0}}>
                  <Text style={{fontSize: 12, fontWeight: 600}} type="secondary">
                    Date of Birth
                  </Text>
                  <DatePicker
                    style={{width: '100%'}}
                    value={formik.values['birthDate'] ? dayjs(formik.values['birthDate']) : null}
                    disabled={isV2Disabled}
                    allowClear
                    maxDate={dayjs()}
                    onChange={(date) =>
                      formik.setFieldValue('birthDate', date ? date.toDate() : null)
                    }
                  />
                </Flex>
                <AntSearchField
                  label="Phone Number"
                  name="phoneNumber"
                  disabled={isV2Disabled}
                  onChange={formik.handleChange}
                  value={formik.values['phoneNumber']}
                />
                <AntSearchField
                  label="Status"
                  name="status"
                  options={status}
                  disabled={isV2Disabled}
                  onChange={formik.handleChange}
                  value={formik.values['status']}
                />
              </>
            ) : null}
          </div>
          <Flex align="center" gap={12} style={{marginTop: 4}}>
            <Button
              type="primary"
              disabled={!formik.values['client']}
              onClick={(e) => {
                formik.handleSubmit(e);
              }}
              loading={loading}
              style={{flex: 1, maxWidth: 215}}>
              {MEMBER_SEARCH.button_text}
            </Button>
            <Button
              type="text"
              aria-label="Reset Form"
              icon={<img src={artifacts.refresh} style={iconSize} alt="" />}
              onClick={() => {
                formik.resetForm({});
                multipleResults && setMultipleResults(null);
                resetTenantDetails();
                noResults && setNoResults(false);
                setPageNumber(0);
              }}>
              Reset
            </Button>
          </Flex>
        </Flex>
      </Card>
      <div ref={searchResultRef}>
        {noResults && <NoSearchResults CSTexts={MEMBER_SEARCH.no_search_result} />}
        {multipleResults && (
          <>
            <MultipleResults results={multipleResults} />
            {loading && (
              <Flex justify="center" style={{marginTop: 32}}>
                <Skeleton active paragraph={{rows: 6}} />
              </Flex>
            )}
          </>
        )}
      </div>
      {error && (
        <SnackBar
          errorMessage={error}
          isSnackBarOpen={isSnackBarOpen}
          closeSnackBar={closeSnackBar}
          clearMessageOnClose={clearMessageOnClose}
        />
      )}
    </Wrapper>
  );
};

export default SearchMember;
